'use client';
import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';

interface LegalModalProps {
  type: 'privacy' | 'terms' | 'refund' | 'contact' | null;
  onClose: () => void;
}

function LegalModal({ type, onClose }: LegalModalProps) {
  const { t } = useLanguage();
  if (!type) return null;

  const content: Record<string, { title: string; body: React.ReactNode }> = {
    privacy: {
      title: t.privacyPolicy,
      body: (
        <div className="flex flex-col gap-4 text-sm" style={{ color: 'var(--foreground)' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>Last updated: August 2026</p>
          <section>
            <h4 className="font-bold mb-1">1. Data We Collect</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>We collect your mobile number and vehicle number exclusively for service job cards and booking updates. No sensitive financial data (card numbers, UPI IDs) is stored on our servers.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">2. Razorpay Payment Processing</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>All online payments are processed securely by Razorpay, a PCI-DSS Level 1 certified payment gateway. We do not store any card, UPI, or banking credentials. Razorpay's privacy policy applies to all payment data.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">3. Data Usage</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Customer details (phone &amp; vehicle number) are used exclusively for service job cards and booking updates. We do not sell or share your data with third parties.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">4. WhatsApp Reminders</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Service reminders are sent via WhatsApp only with your consent. You may opt out at any time by contacting us.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">5. Contact for Privacy</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Email: alokmphs05@gmail.com | WhatsApp: +91 9576551434</p>
          </section>
        </div>
      ),
    },
    terms: {
      title: t.termsConditions,
      body: (
        <div className="flex flex-col gap-4 text-sm" style={{ color: 'var(--foreground)' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>Last updated: August 2026</p>
          <section>
            <h4 className="font-bold mb-1">1. Service Booking</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Online bookings are subject to bay availability. Chaurasiya Motorcycle Workshop reserves the right to reschedule appointments due to operational constraints. Customers will be notified via WhatsApp.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">2. Service Estimation</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Service estimates are indicative. Final charges may vary based on actual parts used and labor required. Admin approval is required before additional work is performed.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">3. Wallet Terms</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Wallet funds are strictly non-transferable and valid only for workshop services and parts. Wallet balance is non-refundable except as per our Refund Policy. The recharge bonus is a promotional offer subject to change.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">4. Parts Replacement</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>All replacement parts are sourced from authorised dealers. Customers may request original or compatible parts. Warranty on parts is as per manufacturer terms.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">5. Digital Wallet Recharge</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Wallet recharges are processed via Razorpay. Recharge amounts are credited instantly upon successful payment. Bonus credits are applied as per current promotional rates.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">6. Liability</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Chaurasiya Motorcycle Workshop is not liable for pre-existing vehicle conditions. Customers are advised to report all known issues at the time of drop-off.</p>
          </section>
        </div>
      ),
    },
    refund: {
      title: t.refundPolicy,
      body: (
        <div className="flex flex-col gap-4 text-sm" style={{ color: 'var(--foreground)' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>Last updated: August 2026</p>
          <section>
            <h4 className="font-bold mb-1">1. Booking Cancellation</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Free booking cancellation is available up to 2 hours prior to the scheduled slot. Cancellations made within 2 hours of the slot are non-refundable.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">2. Wallet Refund (Cancelled Bookings)</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>For bookings paid via wallet, the full amount is refunded instantly to your Chaurasiya Workshop wallet upon cancellation (within the allowed window).</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">3. Online Payment Refund (Razorpay)</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>For online payments via Razorpay (UPI, card, net banking), refunds are processed within 3–5 bank business days as per Razorpay's refund policy. Contact us with your Razorpay payment ID for faster resolution.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">4. Wallet Balance Refund</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>Wallet balance refunds to the original payment source are processed within 7–10 business days. Wallet-to-wallet transfers are not supported.</p>
          </section>
          <section>
            <h4 className="font-bold mb-1">5. Contact for Refunds</h4>
            <p style={{ color: 'var(--muted-foreground)' }}>WhatsApp: +91 9576551434 | Email: alokmphs05@gmail.com</p>
          </section>
        </div>
      ),
    },
    contact: {
      title: t.contactUs,
      body: (
        <div className="flex flex-col gap-4 text-sm" style={{ color: 'var(--foreground)' }}>
          <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)' }}>
            <div className="text-white font-bold text-base mb-0.5">🔧 Chaurasiya Motorcycle Workshop</div>
            <div className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Owner: Alok Kumar Chaurasiya</div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--muted)' }}>
              <span className="text-lg">📍</span>
              <div>
                <div className="font-semibold">Address</div>
                <div style={{ color: 'var(--muted-foreground)' }}>Chaurasiya Motorcycle Workshop</div>
                <div style={{ color: 'var(--muted-foreground)' }}>Lagma, Dumra, Sitamarhi, Bihar — 843302</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--muted)' }}>
              <span className="text-lg">📞</span>
              <div>
                <div className="font-semibold">Phone / WhatsApp</div>
                <a href="tel:+919576551434" className="font-bold" style={{ color: 'var(--accent)' }}>+91 9576551434</a>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--muted)' }}>
              <span className="text-lg">📧</span>
              <div>
                <div className="font-semibold">Email</div>
                <a href="mailto:alokmphs05@gmail.com" style={{ color: 'var(--accent)' }}>alokmphs05@gmail.com</a>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--muted)' }}>
              <span className="text-lg">⏰</span>
              <div>
                <div className="font-semibold">Working Hours</div>
                <div style={{ color: 'var(--muted-foreground)' }}>Monday – Sunday: 08:00 AM – 08:00 PM</div>
                <div className="text-xs mt-0.5" style={{ color: '#16a34a' }}>सातों दिन खुला</div>
              </div>
            </div>
            <a
              href="https://wa.me/919576551434?text=Hello%20Chaurasiya%20Motorcycle%20Workshop%2C%20I%20need%20help."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white transition-all"
              style={{ backgroundColor: '#25D366' }}
            >
              💬 WhatsApp पर संपर्क करें
            </a>
          </div>
        </div>
      ),
    },
  };

  const modal = content[type];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden card-shadow-md"
        style={{ backgroundColor: 'var(--card)', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--primary)' }}>
          <h3 className="font-bold text-white text-base">{modal.title}</h3>
          <button onClick={onClose} className="text-white text-xl font-bold leading-none opacity-70 hover:opacity-100">✕</button>
        </div>
        <div className="overflow-y-auto p-5" style={{ maxHeight: 'calc(85vh - 120px)' }}>
          {modal.body}
        </div>
        <div className="px-5 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <button onClick={onClose} className="btn-primary" style={{ fontSize: '13px', padding: '9px 16px' }}>{t.close}</button>
        </div>
      </div>
    </div>
  );
}

export default function LegalFooter() {
  const { t } = useLanguage();
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'refund' | 'contact' | null>(null);

  return (
    <>
      <LegalModal type={activeModal} onClose={() => setActiveModal(null)} />
      <footer className="w-full mt-8 border-t" style={{ backgroundColor: 'var(--primary)', borderColor: 'rgba(255,255,255,0.1)' }}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 xl:px-10 py-6">
          {/* Workshop Info */}
          <div className="text-center mb-4">
            <div className="text-white font-bold text-sm">🔧 Chaurasiya Motorcycle Workshop</div>
            <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Lagma, Dumra, Sitamarhi, Bihar — 843302</div>
          </div>

          {/* Links Row */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-4">
            {([
              { key: 'contact', label: t.contactUs },
              { key: 'terms', label: t.termsConditions },
              { key: 'privacy', label: t.privacyPolicy },
              { key: 'refund', label: t.refundPolicy },
            ] as const).map((item) => (
              <button
                key={item.key}
                onClick={() => setActiveModal(item.key)}
                className="text-xs font-semibold transition-all hover:underline"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Razorpay Badge */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
              <span className="text-xs">🔒</span>
              <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.poweredBy}</span>
            </div>
          </div>

          {/* Copyright */}
          <p className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{t.footerRights}</p>
        </div>
      </footer>
    </>
  );
}
